/**
 * Cart badge, toast, and reference-style cart drawer.
 *
 * The drawer stays useful without client-side templates: the header bag and
 * checkout are real links/forms. JavaScript adds line editing, product
 * suggestions, wishlist actions, and in-cart variant edits.
 */
(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  const toast = document.querySelector('[data-cart-toast]');
  if (!drawer || typeof drawer.showModal !== 'function') return;

  const q = (selector, root = drawer) => root.querySelector(selector);
  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const el = {
    count: document.querySelector('[data-cart-count]'),
    head: q('.drawer__head'),
    body: q('.drawer__body'),
    lines: q('[data-cart-lines]'),
    empty: q('[data-cart-empty]'),
    foot: q('[data-cart-foot]'),
    checkout: q('.drawer__checkout'),
    checkoutTotal: q('[data-cart-checkout-total]'),
    note: q('[data-cart-note]'),
    shipping: q('[data-cart-shipping]'),
    shippingQualified: q('[data-cart-shipping-qualified]'),
    shippingInvite: q('[data-cart-shipping-invite]'),
    shippingSignup: q('[data-cart-shipping-signup]'),
    bundle: q('[data-cart-bundle]'),
    assurances: q('[data-cart-assurances]'),
    addItems: q('[data-cart-add-items]'),
    picker: q('[data-cart-picker]'),
    pickerBack: q('[data-cart-picker-back]'),
    pickerTitle: q('[data-cart-picker-title]'),
    pickerStatus: q('[data-cart-picker-status]'),
    pickerList: q('[data-cart-picker-list]'),
    status: q('[data-cart-status]'),
    undo: q('[data-cart-undo]'),
    undoMessage: q('[data-cart-undo-message]'),
    undoButton: q('[data-cart-undo-button]'),
    toastImage: toast && toast.querySelector('[data-cart-toast-image]'),
    toastLine: toast && toast.querySelector('[data-cart-toast-line]'),
  };

  const favoritesEnabled = document.documentElement.dataset.favoritesEnabled === 'true';
  const freeShippingCode = String(drawer.dataset.freeShippingCode || '').trim();
  const freeShippingOfferActive = drawer.dataset.freeShippingOfferActive === 'true';
  const customerAcceptsMarketing = drawer.dataset.customerAcceptsMarketing === 'true';
  const wishlistKey = 'mspineapple:wishlist';
  const selectionStorageKey = 'mspineapple:cart-checkout-selection:v1';
  const parkedStorageKey = 'mspineapple:cart-checkout-parked:v1';
  const strings = {
    decrease: drawer.dataset.decrease || 'Decrease quantity',
    increase: drawer.dataset.increase || 'Increase quantity',
    movedToFavorites: drawer.dataset.movedToFavorites || '[[title]] moved to Favorites.',
    removedAlreadySaved: drawer.dataset.removedAlreadySaved || '[[title]] removed. It is already in your Favorites.',
    removedFromCart: drawer.dataset.removedFromCart || '[[title]] removed from your cart.',
  };

  let currentCart = null;
  let lastCartTrigger = null;
  let toastTimer;
  let statusTimer;
  let selectionCartToken = null;
  let excludedLines = [];
  let checkoutBusy = false;
  let restorePromise = null;
  let undoTimer;
  let removedItem = null;
  let discountApplyPromise = null;
  const productCache = new Map();

  const money = (cents, currency) => window.MSPMoney.format(cents, currency);
  const setPageScrollLock = (locked) => {
    document.documentElement.classList.toggle('cart-drawer-open', locked);
  };
  const announce = (message, visible = false) => {
    if (!el.status) return;
    clearTimeout(statusTimer);
    el.status.textContent = message;
    el.status.classList.toggle('is-visible', visible);
    if (visible) {
      statusTimer = setTimeout(() => el.status.classList.remove('is-visible'), 7000);
    }
  };

  const fetchJSON = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
      const error = new Error(`Request failed (${response.status})`);
      try {
        const body = await response.json();
        error.message = body.description || body.message || error.message;
      } catch (_error) {
        // The status code is still a useful fallback.
      }
      throw error;
    }
    return response.json();
  };

  const getCart = () => fetchJSON('/cart.js');
  const getProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetchJSON(`/products/${encodeURIComponent(handle)}.js`));
    }
    return productCache.get(handle);
  };

  const compareAtFor = async (item) => {
    if (!item.handle) return 0;
    try {
      const product = await getProduct(item.handle);
      const variant = product.variants.find((entry) => Number(entry.id) === Number(item.variant_id));
      return (variant && variant.compare_at_price) || 0;
    } catch (_error) {
      return 0;
    }
  };

  const setBadge = (count) => {
    if (!el.count) return;
    el.count.textContent = count;
    el.count.hidden = count === 0;
  };

  const cookieValue = (name) => {
    const prefix = `${name}=`;
    const entry = document.cookie.split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    if (!entry) return '';
    try {
      return decodeURIComponent(entry.slice(prefix.length));
    } catch (_error) {
      return entry.slice(prefix.length);
    }
  };

  const isFreeShippingCode = (value) => (
    Boolean(freeShippingCode)
    && String(value || '').trim().toUpperCase() === freeShippingCode.toUpperCase()
  );

  const cartHasFreeShipping = (cart) => {
    if (!cart) return false;
    if (isFreeShippingCode(cart.attributes?.signup_discount)) return true;
    if (isFreeShippingCode(cookieValue('discount_code'))) return true;
    if ((cart.discount_codes || []).some((discount) => (
      isFreeShippingCode(discount?.code) && discount?.applicable !== false
    ))) return true;
    return (cart.cart_level_discount_applications || []).some((discount) => (
      isFreeShippingCode(discount?.code || discount?.title)
    ));
  };

  const ensureFreeShippingDiscount = () => {
    if (!freeShippingOfferActive || !freeShippingCode || discountApplyPromise) {
      return discountApplyPromise || Promise.resolve();
    }

    const ninetyDays = 90 * 24 * 60 * 60;
    document.cookie = `discount_code=${encodeURIComponent(freeShippingCode)}; path=/; max-age=${ninetyDays}; SameSite=Lax`;
    discountApplyPromise = Promise.allSettled([
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: freeShippingCode,
          attributes: { signup_discount: freeShippingCode },
        }),
      }),
      fetch(`/discount/${encodeURIComponent(freeShippingCode)}`, {
        credentials: 'same-origin',
        redirect: 'follow',
      }),
    ]);
    return discountApplyPromise;
  };

  const renderShipping = (cart) => {
    if (!el.shipping) return;
    const qualified = freeShippingOfferActive
      && (customerAcceptsMarketing || cartHasFreeShipping(cart));
    el.shipping.hidden = !freeShippingOfferActive;
    el.shipping.dataset.state = qualified ? 'qualified' : 'invite';
    if (el.shippingQualified) el.shippingQualified.hidden = !qualified;
    if (el.shippingInvite) el.shippingInvite.hidden = qualified;
  };

  const renderBundle = (cart) => {
    if (!el.bundle) return;
    el.bundle.hidden = cart.item_count === 0;
  };

  const changeLine = async (key, quantity) => {
    const cart = await fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    render(cart);
    return cart;
  };

  const optionNamesFor = (product) => (product.options || []).map((option, index) => (
    typeof option === 'string' ? option : option.name || `Option ${index + 1}`
  ));

  const variantOptionsFor = (variant) => {
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
  };

  const customMeasurementKeys = ['Head measurement', 'Measurement unit'];
  const normalizeOptionToken = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-‑–]/g, ' ')
    .replace(/\s+/g, ' ');
  const isCustomOptionValue = (value) => String(value || '').split('/')
    .some((part) => ['custom', 'bespoke', 'made to measure'].includes(normalizeOptionToken(part)));
  const variantRequiresMeasurement = (variant) => variantOptionsFor(variant).some(isCustomOptionValue);
  const publicPropertiesFor = (properties) => {
    const entries = Object.entries(properties || {}).filter(([name, value]) => (
      name && !name.startsWith('_') && value != null && String(value).trim() !== ''
    ));
    const measurementIndex = entries.findIndex(([name]) => name === 'Head measurement');
    const unitIndex = entries.findIndex(([name]) => name === 'Measurement unit');
    if (measurementIndex === -1 || unitIndex === -1) return entries;

    const measurement = String(entries[measurementIndex][1]).trim();
    const measurementNumber = Number(measurement);
    const unit = String(entries[unitIndex][1]).trim().toLowerCase();
    if (!Number.isFinite(measurementNumber) || measurementNumber <= 0 || !['cm', 'in'].includes(unit)) {
      return entries;
    }

    return entries.flatMap(([name, value], index) => {
      if (index === unitIndex) return [];
      if (index === measurementIndex) return [[name, `${measurement} ${unit}`]];
      return [[name, value]];
    });
  };
  const hasValidMeasurementProperties = (properties) => {
    const measurement = Number(properties?.['Head measurement']);
    const unit = String(properties?.['Measurement unit'] || '').toLowerCase();
    return Number.isFinite(measurement) && measurement > 0 && ['cm', 'in'].includes(unit);
  };

  const propertiesForVariant = (item, variant, proposedProperties = item.properties) => {
    const properties = { ...(proposedProperties || {}) };
    if (!variantRequiresMeasurement(variant)) {
      customMeasurementKeys.forEach((key) => delete properties[key]);
      return properties;
    }

    if (!hasValidMeasurementProperties(properties)) {
      throw new Error('Choose this custom option on the product page to enter a head measurement.');
    }
    return properties;
  };

  const stableValue = (value) => {
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce((result, key) => {
        result[key] = stableValue(value[key]);
        return result;
      }, {});
    }
    return value;
  };

  const sellingPlanIdFor = (line) => Number(
    line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || 0
  );

  const lineSignature = (variantId, properties, sellingPlanId) => JSON.stringify([
    Number(variantId),
    stableValue(properties || {}),
    Number(sellingPlanId || 0),
  ]);

  const signatureForLine = (line) => lineSignature(
    line.variant_id || line.id,
    line.properties,
    sellingPlanIdFor(line)
  );

  const linesBySignature = (cart, signature) => (
    cart?.items?.filter((line) => signatureForLine(line) === signature) || []
  );

  const resolveUniqueLine = (cart, signature, preferredKey) => {
    const matches = linesBySignature(cart, signature);
    if (preferredKey) {
      const preferredMatches = matches.filter((line) => line.key === preferredKey);
      if (preferredMatches.length === 1) return { line: preferredMatches[0], matches };
      if (preferredMatches.length > 1) return { line: null, matches };
    }
    return { line: matches.length === 1 ? matches[0] : null, matches };
  };

  const totalQuantity = (lines) => lines.reduce((total, line) => total + line.quantity, 0);

  const readSessionJSON = (key) => {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const writeSessionJSON = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const removeSessionValue = (key) => {
    try { window.sessionStorage.removeItem(key); } catch (_error) { /* Storage is optional. */ }
  };

  const payloadForLine = (line) => {
    const payload = {
      id: Number(line.variant_id || line.id),
      quantity: Number(line.quantity),
    };
    if (line.properties && Object.keys(line.properties).length) {
      payload.properties = stableValue(line.properties);
    }
    const sellingPlan = sellingPlanIdFor(line);
    if (sellingPlan) payload.selling_plan = sellingPlan;
    return payload;
  };

  const quantitiesBySignature = (lines) => lines.reduce((result, line) => {
    const signature = line.signature || signatureForLine(line);
    result[signature] = (result[signature] || 0) + Number(line.quantity || 0);
    return result;
  }, {});

  const sameSignatureQuantities = (left, right) => {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return leftKeys.length === rightKeys.length
      && leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
  };

  const persistSelection = () => {
    if (!selectionCartToken || !excludedLines.length) {
      removeSessionValue(selectionStorageKey);
      return;
    }
    writeSessionJSON(selectionStorageKey, {
      cartToken: selectionCartToken,
      excluded: excludedLines,
    });
  };

  const hydrateSelection = (cart) => {
    if (selectionCartToken === cart.token) return;
    selectionCartToken = cart.token;
    const stored = readSessionJSON(selectionStorageKey);
    excludedLines = stored?.cartToken === cart.token && Array.isArray(stored.excluded)
      ? stored.excluded.filter((line) => line && line.key && line.signature)
      : [];
    if (stored && stored.cartToken !== cart.token) removeSessionValue(selectionStorageKey);
  };

  const reconcileSelection = (cart) => {
    hydrateSelection(cart);
    const usedKeys = new Set();
    const next = [];

    excludedLines.forEach((record) => {
      let match = cart.items.find((line) => (
        !usedKeys.has(line.key)
        && line.key === record.key
        && signatureForLine(line) === record.signature
      ));
      if (!match) {
        const candidates = cart.items.filter((line) => (
          !usedKeys.has(line.key) && signatureForLine(line) === record.signature
        ));
        match = candidates.find((line) => Number(line.quantity) === Number(record.quantity))
          || candidates[0];
      }
      if (!match) return;
      usedKeys.add(match.key);
      next.push({
        key: match.key,
        signature: record.signature,
        quantity: Number(match.quantity),
      });
    });

    if (JSON.stringify(next) !== JSON.stringify(excludedLines)) {
      excludedLines = next;
      persistSelection();
    }
  };

  const lineIsSelected = (line) => !excludedLines.some((record) => record.key === line.key);

  const selectedItemsFor = (cart) => {
    reconcileSelection(cart);
    return cart.items.filter(lineIsSelected);
  };

  const toggleLineSelection = (item) => {
    const existing = excludedLines.findIndex((record) => record.key === item.key);
    if (existing >= 0) {
      excludedLines.splice(existing, 1);
    } else {
      excludedLines.push({
        key: item.key,
        signature: signatureForLine(item),
        quantity: Number(item.quantity),
      });
    }
    persistSelection();
  };

  const transferExcludedSelection = (oldItem, targetSignature, cart) => {
    const recordIndex = excludedLines.findIndex((record) => record.key === oldItem.key);
    if (recordIndex < 0) return;
    const [record] = excludedLines.splice(recordIndex, 1);
    const replacement = cart.items.find((line) => (
      signatureForLine(line) === targetSignature
      && !excludedLines.some((excluded) => excluded.key === line.key)
    ));
    if (replacement) {
      excludedLines.push({
        key: replacement.key,
        signature: targetSignature,
        quantity: Number(replacement.quantity),
      });
    } else {
      excludedLines.push(record);
    }
    persistSelection();
  };

  const restoreSignatureTotal = async (cart, signature, desiredTotal, preferredKey) => {
    const matches = linesBySignature(cart, signature);
    const currentTotal = totalQuantity(matches);
    if (currentTotal === desiredTotal) return cart;
    if (currentTotal < desiredTotal) {
      throw new Error('The previous target quantity can no longer be restored safely.');
    }

    const uniqueKeys = new Set(matches.map((line) => line.key));
    if (uniqueKeys.size !== matches.length) {
      throw new Error('Split cart lines do not have unique keys.');
    }

    const preferred = preferredKey && matches.find((line) => line.key === preferredKey);
    const ordered = preferred
      ? [preferred, ...matches.filter((line) => line !== preferred)]
      : matches;
    let remaining = desiredTotal;
    const updates = {};
    ordered.forEach((line) => {
      const keep = Math.min(line.quantity, remaining);
      updates[line.key] = keep;
      remaining -= keep;
    });
    if (remaining !== 0) throw new Error('The target quantity could not be distributed safely.');

    await fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const latestCart = await getCart();
    if (totalQuantity(linesBySignature(latestCart, signature)) !== desiredTotal) {
      throw new Error('The restored target quantity could not be verified.');
    }
    return latestCart;
  };

  const updateLineProperties = async (item, nextVariant, nextProperties, line, editor) => {
    line.classList.add('is-busy');
    try {
      const beforeChange = await getCart();
      const exactMatches = beforeChange.items.filter((entry) => entry.key === item.key);
      if (exactMatches.length !== 1) {
        throw new Error('This cart item changed. Refresh the cart and try again.');
      }

      const originalLine = exactMatches[0];
      const originalSignature = signatureForLine(originalLine);
      if (originalSignature !== signatureForLine(item)) {
        throw new Error('This cart item changed. Refresh the cart and try again.');
      }

      const sellingPlan = sellingPlanIdFor(originalLine);
      const targetSignature = lineSignature(nextVariant.id, nextProperties, sellingPlan);
      if (targetSignature === originalSignature) {
        line.classList.remove('is-busy');
        return true;
      }
      if (linesBySignature(beforeChange, targetSignature).some((entry) => entry.key !== originalLine.key)) {
        throw new Error('An identical cart item already exists. Update it separately.');
      }

      const payload = {
        id: originalLine.key,
        quantity: originalLine.quantity,
        properties: nextProperties,
      };
      const cart = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const updatedMatches = linesBySignature(cart, targetSignature);
      if (updatedMatches.length !== 1 || updatedMatches[0].quantity !== originalLine.quantity) {
        throw new Error('The updated measurement could not be verified.');
      }
      if (linesBySignature(cart, originalSignature).length) {
        throw new Error('The previous measurement is still in the cart.');
      }

      render(cart);
      announce('Cart item updated.');
      return true;
    } catch (error) {
      try {
        const serverCart = await getCart();
        const sellingPlan = sellingPlanIdFor(item);
        const targetSignature = lineSignature(nextVariant.id, nextProperties, sellingPlan);
        const updatedMatches = linesBySignature(serverCart, targetSignature);
        if (
          updatedMatches.length === 1
          && updatedMatches[0].quantity === item.quantity
          && !linesBySignature(serverCart, signatureForLine(item)).length
        ) {
          render(serverCart);
          announce('Cart item updated.');
          return true;
        }
      } catch (_reconciliationError) {
        // The original error remains the most useful inline explanation.
      }
      line.classList.remove('is-busy');
      const status = editor ? q('[data-variant-status]', editor) : null;
      if (status) status.textContent = error.message || 'That item could not be updated.';
      announce(error.message || 'That item could not be updated.', true);
      return false;
    }
  };

  const replaceVariant = async (item, nextVariant, line, editor, proposedProperties) => {
    const nextProperties = propertiesForVariant(item, nextVariant, proposedProperties);
    if (Number(nextVariant.id) === Number(item.variant_id)) {
      return updateLineProperties(item, nextVariant, nextProperties, line, editor);
    }

    line.classList.add('is-busy');
    let addAttempted = false;
    let addSucceeded = false;
    const sellingPlan = sellingPlanIdFor(item);
    const originalSignature = signatureForLine(item);
    let targetSignature = '';
    let previousTargetTotal = 0;
    let previousTargetKey = null;
    let selectionSource = item;
    try {
      targetSignature = lineSignature(nextVariant.id, nextProperties, sellingPlan);
      const beforeAdd = await getCart();
      reconcileSelection(beforeAdd);
      const originalResolution = resolveUniqueLine(beforeAdd, originalSignature, item.key);
      const originalBeforeAdd = originalResolution.line;
      if (originalResolution.matches.length !== 1 || !originalBeforeAdd || originalBeforeAdd.key !== item.key) {
        throw new Error('The original option is split across cart lines. Please refresh and try again.');
      }
      selectionSource = originalBeforeAdd;

      const targetMatchesBeforeAdd = linesBySignature(beforeAdd, targetSignature);
      if (targetMatchesBeforeAdd.length > 1) {
        throw new Error('The target option is split across cart lines. Please refresh and try again.');
      }
      const targetBeforeAdd = targetMatchesBeforeAdd[0] || null;
      if (targetBeforeAdd && lineIsSelected(originalBeforeAdd) !== lineIsSelected(targetBeforeAdd)) {
        throw new Error('Select both matching cart items the same way before combining their options.');
      }
      previousTargetTotal = targetBeforeAdd?.quantity || 0;
      previousTargetKey = targetBeforeAdd?.key || null;

      const nextLine = {
        id: nextVariant.id,
        quantity: originalBeforeAdd.quantity,
      };
      if (Object.keys(nextProperties).length) {
        nextLine.properties = nextProperties;
      }
      if (sellingPlan) nextLine.selling_plan = sellingPlan;

      // Add first: if the requested combination is unavailable, the existing
      // line remains untouched. Only remove it after Shopify accepts the add.
      addAttempted = true;
      await fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [nextLine] }),
      });
      addSucceeded = true;

      // Automatic discounts can change every line key during the add. Resolve
      // the original line again from a fresh cart before attempting removal.
      const afterAdd = await getCart();
      const currentOriginalMatches = linesBySignature(afterAdd, originalSignature);
      if (!currentOriginalMatches.length) {
        transferExcludedSelection(selectionSource, targetSignature, afterAdd);
        render(afterAdd);
        announce('Cart option updated.');
        return true;
      }

      if (currentOriginalMatches.length !== 1) {
        try {
          const restoredCart = await restoreSignatureTotal(
            afterAdd,
            targetSignature,
            previousTargetTotal,
            previousTargetKey
          );
          render(restoredCart);
          announce('We could not update that option because the original item is split across cart lines.', true);
        } catch (_recoveryError) {
          try { render(await getCart()); } catch (_refreshError) { line.classList.remove('is-busy'); }
          announce('We could not confirm the option change. Please review your cart quantities.', true);
        }
        return false;
      }

      const currentOriginal = currentOriginalMatches[0];

      const cart = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentOriginal.key, quantity: 0 }),
      });
      if (linesBySignature(cart, originalSignature).length) {
        throw new Error('The original cart item could not be removed.');
      }
      transferExcludedSelection(selectionSource, targetSignature, cart);
      render(cart);
      announce('Cart option updated.');
      return true;
    } catch (error) {
      if (!addSucceeded) {
        if (!addAttempted) {
          line.classList.remove('is-busy');
          const status = editor ? q('[data-variant-status]', editor) : null;
          if (status) status.textContent = error.message || 'That option could not be updated.';
          announce('That option could not be updated.', true);
          return false;
        }

        // The request may have reached Shopify even if its response was lost
        // or could not be parsed. Reconcile from current server state and use
        // only target keys returned by that fresh cart response.
        try {
          let serverCart = await getCart();
          const currentTargetTotal = totalQuantity(linesBySignature(serverCart, targetSignature));
          if (currentTargetTotal !== previousTargetTotal) {
            serverCart = await restoreSignatureTotal(
              serverCart,
              targetSignature,
              previousTargetTotal,
              previousTargetKey
            );
          }
          render(serverCart);
          announce('We could not update that option. Your cart was restored.', true);
        } catch (_reconciliationError) {
          try { render(await getCart()); } catch (_refreshError) { line.classList.remove('is-busy'); }
          announce('We could not confirm the option change. Please review your cart quantities.', true);
        }
        return false;
      }

      try {
        // A failed/aborted response does not prove removal failed. Check the
        // server first: if the original signature is gone, the change landed.
        let serverCart = await getCart();
        const currentOriginalMatches = linesBySignature(serverCart, originalSignature);
        if (!currentOriginalMatches.length) {
          transferExcludedSelection(selectionSource, targetSignature, serverCart);
          render(serverCart);
          announce('Cart option updated.');
          return true;
        }

        // The original remains (possibly split), so restore the complete
        // target signature to its exact pre-add total in one update request.
        serverCart = await restoreSignatureTotal(
          serverCart,
          targetSignature,
          previousTargetTotal,
          previousTargetKey
        );
        render(serverCart);
        announce('We could not update that option. Your original item is still in the cart.', true);
      } catch (_recoveryError) {
        try { render(await getCart()); } catch (_refreshError) { line.classList.remove('is-busy'); }
        announce('We could not confirm the option change. Please review your cart quantities.', true);
      }
      return false;
    }
  };

  const populateVariantEditor = async (item, line, editor, toggle) => {
    editor.hidden = false;
    editor.replaceChildren(create('p', 'drawer__variant-status', 'Loading options…'));
    editor.firstChild.dataset.variantStatus = '';
    toggle.setAttribute('aria-expanded', 'true');

    try {
      const product = await getProduct(item.handle);
      const optionNames = optionNamesFor(product);
      const currentVariant = product.variants.find((variant) => Number(variant.id) === Number(item.variant_id));
      if (!optionNames.length || !currentVariant) throw new Error('Options are unavailable for this item.');

      editor.replaceChildren();
      const selects = optionNames.map((name, index) => {
        const label = create('label', 'drawer__variant-field');
        label.appendChild(create('span', '', name));
        const select = create('select', 'drawer__variant-select');
        select.setAttribute('aria-label', name);
        const values = [...new Set(product.variants.map((variant) => variantOptionsFor(variant)[index]).filter(Boolean))];
        values.forEach((value) => {
          const option = create('option', '', value);
          option.value = value;
          if (variantOptionsFor(currentVariant)[index] === value) option.selected = true;
          select.appendChild(option);
        });
        label.appendChild(select);
        editor.appendChild(label);
        return select;
      });

      const status = create('p', 'drawer__variant-status');
      status.dataset.variantStatus = '';
      const actions = create('div', 'drawer__variant-actions');
      const cancel = create('button', 'drawer__variant-cancel', 'Cancel');
      cancel.type = 'button';
      const apply = create('button', 'drawer__variant-apply', 'Update item');
      apply.type = 'button';
      actions.append(cancel, apply);
      editor.append(status, actions);

      let selectedVariant = currentVariant;
      const syncSelection = () => {
        const values = selects.map((select) => select.value);
        selectedVariant = product.variants.find((variant) => (
          variantOptionsFor(variant).every((value, index) => value === values[index])
        ));
        const unchanged = Number(selectedVariant?.id) === Number(item.variant_id);
        const missingMeasurement = selectedVariant && variantRequiresMeasurement(selectedVariant)
          && !hasValidMeasurementProperties(item.properties);
        apply.disabled = !selectedVariant || !selectedVariant.available || unchanged || missingMeasurement;
        status.textContent = missingMeasurement
          ? 'Choose this custom option on the product page to enter a head measurement.'
          : !selectedVariant || !selectedVariant.available
          ? 'This combination is sold out.'
          : unchanged ? 'Current selection' : '';
      };
      selects.forEach((select) => select.addEventListener('change', syncSelection));
      syncSelection();

      cancel.addEventListener('click', () => {
        editor.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      });
      apply.addEventListener('click', () => replaceVariant(item, selectedVariant, line, editor));
      selects[0]?.focus();
    } catch (error) {
      editor.replaceChildren(create('p', 'drawer__variant-status', error.message || 'Options could not be loaded.'));
    }
  };

  const staticSvg = (markup, className) => {
    const holder = create('span', className);
    holder.innerHTML = markup;
    return holder;
  };

  const buildLine = (item, currency) => {
    const isSelected = lineIsSelected(item);
    const line = create('li', `drawer__line${isSelected ? '' : ' is-excluded'}`);

    const included = create('button', 'drawer__included');
    included.type = 'button';
    included.dataset.cartSelect = item.key;
    included.setAttribute('aria-pressed', String(isSelected));
    included.setAttribute('aria-label', `Include ${item.product_title} in checkout`);
    included.appendChild(create('span', 'drawer__included-icon'));

    const mediaWrap = create('div', 'drawer__line-media-wrap');
    const media = create('a', 'drawer__line-media');
    media.href = item.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (item.image) {
      const image = create('img');
      image.src = item.image;
      image.alt = '';
      image.width = 360;
      image.height = 360;
      media.appendChild(image);
    }
    const view = create('a', 'drawer__view-product');
    view.href = item.url;
    view.setAttribute('aria-label', `View ${item.product_title}`);
    view.appendChild(staticSvg(
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 5h10v12H5zM9 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/><path d="m6.5 15 3-3 2 2 2-2 1.5 1.5" stroke-linejoin="round"/><path d="M15 9h4v10H9v-2"/></svg>',
      'drawer__view-icon'
    ));
    mediaWrap.append(media, view);

    const info = create('div', 'drawer__line-info');
    const main = create('div', 'drawer__line-main');
    const details = create('div', 'drawer__line-details');
    const title = create('a', 'drawer__line-title', item.product_title);
    title.href = item.url;
    details.appendChild(title);

    let variantToggle = null;
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantToggle = create('button', 'drawer__line-variant');
      variantToggle.type = 'button';
      variantToggle.setAttribute('aria-expanded', 'false');
      variantToggle.setAttribute('aria-label', `Change color or size: ${item.variant_title}`);
      variantToggle.append(
        create('span', '', item.variant_title),
        staticSvg('<svg viewBox="0 0 14 9" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m1 1 6 6 6-6"/></svg>', 'drawer__variant-chevron')
      );
    }

    const visibleProperties = publicPropertiesFor(item.properties);
    const measurement = Number(item.properties?.['Head measurement']);
    const measurementUnit = String(item.properties?.['Measurement unit'] || '').trim().toLowerCase();
    const measurementIndex = Number.isFinite(measurement) && measurement > 0 && ['cm', 'in'].includes(measurementUnit)
      ? visibleProperties.findIndex(([name]) => name === 'Head measurement')
      : -1;
    const meta = create('div', 'drawer__line-meta');
    if (variantToggle) meta.appendChild(variantToggle);
    if (measurementIndex !== -1) {
      meta.appendChild(create(
        'span',
        `drawer__line-measurement${variantToggle ? ' has-separator' : ''}`,
        `Head measurement: ${visibleProperties[measurementIndex][1]}`
      ));
    }
    if (meta.childNodes.length) details.appendChild(meta);

    const remainingProperties = measurementIndex === -1
      ? visibleProperties
      : visibleProperties.filter((_, index) => index !== measurementIndex);
    if (remainingProperties.length) {
      const properties = create('dl', 'drawer__line-properties');
      remainingProperties.forEach(([name, value]) => {
        const property = create('div', 'drawer__line-property');
        const label = create('dt', '', `${name}:`);
        const content = create('dd', '', String(value));
        property.append(label, content);
        properties.appendChild(property);
      });
      details.appendChild(properties);
    }

    const prices = create('div', 'drawer__line-prices');
    const now = create('p', 'drawer__line-price', money(item.final_line_price, currency));
    prices.appendChild(now);
    const strike = (amount) => {
      if (prices.querySelector('.drawer__line-was')) return;
      now.classList.add('is-sale');
      prices.appendChild(create('s', 'drawer__line-was', money(amount, currency)));
    };
    if (item.original_line_price && item.original_line_price > item.final_line_price) {
      strike(item.original_line_price);
    } else {
      compareAtFor(item).then((compareAt) => {
        if (compareAt > item.price) strike(compareAt * item.quantity);
      });
    }
    main.append(details, prices);
    info.appendChild(main);

    if (variantToggle) {
      // Editing a line reuses the quick-view popup (size pills + colour
      // swatches, responsive sheet) rather than a bespoke dropdown editor.
      // onApply swaps the variant on this line via the existing logic.
      variantToggle.addEventListener('click', () => {
        if (window.MSPQuickView && typeof window.MSPQuickView.openEdit === 'function') {
          window.MSPQuickView.openEdit({
            handle: item.handle,
            href: item.url,
            variantId: item.variant_id,
            properties: { ...(item.properties || {}) },
            onApply: (nextVariant, nextProperties) => (
              replaceVariant(item, nextVariant, line, null, nextProperties)
            ),
          });
        } else {
          announce('Cart options could not be loaded. Please try again.', true);
        }
      });
    }

    const controls = create('div', 'drawer__line-controls');
    const qty = create('div', 'drawer__qty');
    const down = create('button');
    down.type = 'button';
    down.dataset.down = '';
    down.setAttribute(
      'aria-label',
      item.quantity === 1 ? `Remove ${item.product_title} from cart` : strings.decrease
    );
    if (item.quantity === 1) {
      down.appendChild(staticSvg(
        '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4.5 6h11M8 3.75h4M6.25 6l.6 10h6.3l.6-10M8.25 8.5v5M11.75 8.5v5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      ));
    } else {
      down.textContent = '−';
    }
    const quantity = create('span', '', String(item.quantity));
    quantity.dataset.qty = '';
    const up = create('button', '', '+');
    up.type = 'button';
    up.dataset.up = '';
    up.setAttribute('aria-label', strings.increase);
    qty.append(down, quantity, up);
    let saveLater = null;
    controls.appendChild(qty);
    if (favoritesEnabled) {
      saveLater = create('button', 'drawer__save-later');
      saveLater.type = 'button';
      saveLater.dataset.saveForLater = '';
      saveLater.setAttribute('aria-label', `Save ${item.product_title} for later in Favorites`);
      saveLater.append(
        staticSvg(
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" stroke-linejoin="round"/></svg>',
          'drawer__save-later-icon'
        ),
        create('span', '', 'Save for later')
      );
      controls.appendChild(saveLater);
    }
    info.appendChild(controls);

    const busy = (task) => async () => {
      line.classList.add('is-busy');
      try {
        await task();
      } catch (error) {
        line.classList.remove('is-busy');
        announce(error.message || 'Cart could not be updated.', true);
      }
    };
    down.addEventListener('click', () => {
      if (item.quantity === 1) {
        busy(() => removeFromCart(item))();
        return;
      }
      busy(() => changeLine(item.key, item.quantity - 1))();
    });
    up.addEventListener('click', busy(() => changeLine(item.key, item.quantity + 1)));
    saveLater?.addEventListener('click', busy(() => moveToFavorites(item)));
    included.addEventListener('click', () => {
      toggleLineSelection(item);
      const nowSelected = lineIsSelected(item);
      render(currentCart);
      const nextToggle = Array.from(el.lines.querySelectorAll('[data-cart-select]'))
        .find((button) => button.dataset.cartSelect === item.key);
      nextToggle?.focus();
      // Screen-reader only. The basket control and the checkout total both show
      // this on screen already, and the toast rendered on top of the total it
      // was reporting, hiding the one number the shopper needed to see.
      announce(`${item.product_title} ${nowSelected ? 'included in' : 'excluded from'} checkout.`);
    });

    line.append(included, mediaWrap, info);
    return line;
  };

  const render = (cart) => {
    currentCart = cart;
    const selectedItems = selectedItemsFor(cart);
    const selectedTotal = selectedItems.reduce((total, item) => total + item.final_line_price, 0);
    setBadge(cart.item_count);
    el.lines.replaceChildren();
    const isEmpty = cart.items.length === 0;
    el.empty.hidden = !isEmpty;
    el.foot.hidden = isEmpty;
    el.assurances.hidden = isEmpty;
    cart.items.forEach((item) => el.lines.appendChild(buildLine(item, cart.currency)));
    if (el.checkoutTotal) el.checkoutTotal.textContent = money(selectedTotal, cart.currency);
    if (el.checkout) {
      el.checkout.disabled = !selectedItems.length || checkoutBusy;
      el.checkout.setAttribute(
        'aria-label',
        selectedItems.length ? `Checkout selected items, ${money(selectedTotal, cart.currency)}` : 'Select an item to checkout'
      );
    }
    if (el.note && document.activeElement !== el.note) el.note.value = cart.note || '';
    renderShipping(cart);
    renderBundle(cart);
  };

  /** Save a product handle in the same array used by the Favorites hearts. */
  const saveToWishlist = (handle) => {
    if (!handle) return 'unavailable';
    try {
      const stored = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      const handles = Array.isArray(stored)
        ? stored.filter((saved) => typeof saved === 'string' && saved)
        : [];
      if (handles.includes(handle)) return 'exists';
      window.localStorage.setItem(wishlistKey, JSON.stringify([handle, ...handles]));
      return 'saved';
    } catch (_error) {
      // Do not remove an item after a failed Favorites write.
      return 'unavailable';
    }
  };

  const moveToFavorites = async (item) => {
    hideUndo();
    const result = saveToWishlist(item.handle);
    if (result === 'unavailable') {
      throw new Error('This item could not be saved to Favorites. Please try again.');
    }
    try {
      await changeLine(item.key, 0);
    } catch (error) {
      if (result === 'saved') {
        try {
          const handles = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
          window.localStorage.setItem(
            wishlistKey,
            JSON.stringify(Array.isArray(handles) ? handles.filter((handle) => handle !== item.handle) : [])
          );
        } catch (_storageError) { /* The cart error remains the useful failure. */ }
      }
      throw error;
    }

    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { handle: item.handle, saved: true, source: 'cart' },
    }));

    const template = result === 'saved' ? strings.movedToFavorites : strings.removedAlreadySaved;
    // The Favorites bar is the visual confirmation. Keep the full message in
    // the live region for assistive technology without covering cart actions.
    announce(template.replace('[[title]]', item.product_title));
  };

  function hideUndo() {
    clearTimeout(undoTimer);
    removedItem = null;
    if (el.undo) el.undo.hidden = true;
    if (el.undoMessage) el.undoMessage.textContent = '';
    if (el.undoButton) el.undoButton.disabled = false;
  }

  const showUndo = (item) => {
    if (!el.undo || !el.undoMessage) return;
    clearTimeout(undoTimer);
    el.undoMessage.textContent = strings.removedFromCart.replace('[[title]]', item.title);
    el.undo.hidden = false;
    el.undoButton?.focus();
    undoTimer = setTimeout(hideUndo, 7000);
  };

  const removeFromCart = async (item) => {
    hideUndo();
    const snapshot = {
      payload: payloadForLine(item),
      signature: signatureForLine(item),
      title: item.product_title,
    };
    const cart = await changeLine(item.key, 0);
    snapshot.remainingQuantity = totalQuantity(linesBySignature(cart, snapshot.signature));
    removedItem = snapshot;
    showUndo(snapshot);
    announce(strings.removedFromCart.replace('[[title]]', item.product_title));
  };

  const undoRemove = async () => {
    if (!removedItem || el.undoButton?.disabled) return;
    const snapshot = removedItem;
    if (el.undoButton) el.undoButton.disabled = true;
    try {
      const before = await getCart();
      const previousTotal = totalQuantity(linesBySignature(before, snapshot.signature));
      const expectedTotal = snapshot.remainingQuantity + snapshot.payload.quantity;
      if (previousTotal >= expectedTotal) {
        hideUndo();
        render(before);
        announce(`${snapshot.title} restored to your cart.`);
        return;
      }
      if (previousTotal !== snapshot.remainingQuantity) {
        throw new Error('Your cart changed before this item could be restored.');
      }
      await fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [snapshot.payload] }),
      });
      const cart = await getCart();
      const restoredTotal = totalQuantity(linesBySignature(cart, snapshot.signature));
      if (restoredTotal !== expectedTotal) {
        throw new Error('The removed item could not be restored safely.');
      }
      hideUndo();
      render(cart);
      announce(`${snapshot.title} restored to your cart.`);
    } catch (error) {
      if (el.undoButton) el.undoButton.disabled = false;
      announce(error.message || 'The removed item could not be restored.', true);
    }
  };

  const readWishlist = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(stored) ? [...new Set(stored.filter((handle) => typeof handle === 'string'))] : [];
    } catch (_error) {
      return [];
    }
  };

  const productImage = (product) => {
    const image = product.featured_image || product.images?.[0];
    return typeof image === 'string' ? image : image?.src;
  };

  const addSuggestedProduct = async (product, select, button) => {
    const variantId = Number(select?.value || product.variants.find((variant) => variant.available)?.id);
    if (!variantId) return;
    const variant = product.variants.find((candidate) => Number(candidate.id) === variantId);
    if (variant && variantRequiresMeasurement(variant)) {
      const productUrl = new URL(product.url || `/products/${product.handle}`, window.location.origin);
      productUrl.searchParams.set('variant', String(variant.id));
      window.location.assign(productUrl.href);
      return;
    }
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      await fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      render(await getCart());
      closePicker();
      announce(`${product.title} added to your cart.`);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add to cart';
      el.pickerStatus.textContent = error.message || 'This item could not be added.';
    }
  };

  const buildPickerProduct = (product) => {
    const item = create('li', 'drawer__picker-item');
    const imageLink = create('a', 'drawer__picker-image');
    imageLink.href = product.url || `/products/${product.handle}`;
    const source = productImage(product);
    if (source) {
      const image = create('img');
      image.src = source;
      image.alt = '';
      image.width = 180;
      image.height = 180;
      imageLink.appendChild(image);
    }
    const details = create('div', 'drawer__picker-details');
    const title = create('a', 'drawer__picker-product-title', product.title);
    title.href = imageLink.href;
    details.appendChild(title);

    const available = product.variants.filter((variant) => variant.available);
    let select = null;
    if (available.length > 1 || (available[0] && available[0].title !== 'Default Title')) {
      select = create('select', 'drawer__picker-select');
      select.setAttribute('aria-label', `Options for ${product.title}`);
      available.forEach((variant) => {
        const option = create('option', '', variant.title);
        option.value = variant.id;
        select.appendChild(option);
      });
      details.appendChild(select);
    }
    const add = create('button', 'drawer__picker-add', 'Add to cart');
    add.type = 'button';
    const syncAddLabel = () => {
      const variantId = Number(select?.value || available[0]?.id);
      const variant = available.find((candidate) => Number(candidate.id) === variantId);
      add.textContent = variant && variantRequiresMeasurement(variant) ? 'Enter measurement' : 'Add to cart';
    };
    select?.addEventListener('change', syncAddLabel);
    add.addEventListener('click', () => addSuggestedProduct(product, select, add));
    syncAddLabel();
    details.appendChild(add);
    item.append(imageLink, details);
    return item;
  };

  const wishlistProducts = async (handles) => {
    const results = await Promise.allSettled(handles.map((handle) => getProduct(handle)));
    return results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
  };

  const relatedProducts = async () => {
    const firstItem = currentCart?.items?.[0];
    if (!firstItem) return [];
    const data = await fetchJSON(
      `/recommendations/products.json?product_id=${encodeURIComponent(firstItem.product_id)}&limit=8&intent=related`
    );
    return data.products || [];
  };

  const openPicker = async () => {
    if (!currentCart?.items?.length) return;
    el.head.inert = true;
    el.body.inert = true;
    el.addItems.setAttribute('aria-expanded', 'true');
    el.picker.hidden = false;
    el.pickerList.replaceChildren();
    el.pickerTitle.textContent = 'Add Items';
    el.pickerStatus.textContent = 'Loading items…';
    el.pickerBack.focus();

    try {
      const savedHandles = favoritesEnabled ? readWishlist() : [];
      let products = savedHandles.length ? await wishlistProducts(savedHandles) : [];
      let fromWishlist = favoritesEnabled
        && products.some((product) => product.variants?.some((variant) => variant.available));
      if (!fromWishlist) products = await relatedProducts();

      const cartProductIds = new Set(currentCart.items.map((item) => Number(item.product_id)));
      const eligible = (items) => {
        const unique = new Map();
        items.forEach((product) => {
          if (!cartProductIds.has(Number(product.id)) && product.variants?.some((variant) => variant.available)) {
            unique.set(Number(product.id), product);
          }
        });
        return [...unique.values()].slice(0, 6);
      };
      let visible = eligible(products);
      if (!visible.length && fromWishlist) {
        visible = eligible(await relatedProducts());
        fromWishlist = false;
      }
      el.pickerTitle.textContent = fromWishlist ? 'Your Wishlist' : 'You May Also Like';
      el.pickerStatus.textContent = visible.length ? '' : 'No additional items are available right now.';
      visible.forEach((product) => el.pickerList.appendChild(buildPickerProduct(product)));
    } catch (_error) {
      el.pickerStatus.textContent = 'Suggestions could not be loaded. Please try again.';
    }
  };

  function closePicker() {
    if (el.picker.hidden) return;
    el.picker.hidden = true;
    el.head.inert = false;
    el.body.inert = false;
    el.addItems.setAttribute('aria-expanded', 'false');
    el.addItems.focus();
  }

  if (el.note) {
    el.note.addEventListener('change', () => {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: el.note.value }),
      });
    });
  }

  const parkedDesiredQuantities = (parked) => {
    const desired = { ...(parked.expectedSelected || {}) };
    (parked.excluded || []).forEach((line) => {
      desired[line.signature] = (desired[line.signature] || 0) + Number(line.quantity || 0);
    });
    return desired;
  };

  const restoreParkedCart = async () => {
    if (restorePromise) return restorePromise;
    const initiallyParked = readSessionJSON(parkedStorageKey);
    if (!initiallyParked?.cartToken || !Array.isArray(initiallyParked.excluded)) return null;
    if (!navigator.locks?.request) {
      throw new Error('Saved items need a browser with secure cart locking before they can be restored automatically.');
    }

    restorePromise = navigator.locks.request('mspineapple:cart-checkout-restore', async () => {
      // sessionStorage can be cloned when a tab is duplicated. Re-read only
      // after acquiring the origin-wide lock, then re-fetch the server cart,
      // so a second tab observes the first tab's completed restore and adds 0.
      let parked = readSessionJSON(parkedStorageKey);
      if (!parked?.cartToken || !Array.isArray(parked.excluded)) return null;
      let cart = await getCart();
      if (cart.token !== parked.cartToken) {
        if (parked.stage === 'pending') {
          throw new Error('The cart changed before checkout could be confirmed. Saved item details were retained; please review your cart.');
        }
        // Checkout can rotate the cart token after the selected items are
        // purchased. The parked lines still belong to the shopper, so carry
        // them into the new cart instead of silently discarding their payload.
        parked = {
          ...parked,
          cartToken: cart.token,
          stage: 'parked',
          expectedSelected: {},
        };
        if (!writeSessionJSON(parkedStorageKey, parked)) {
          throw new Error('Your excluded items could not be moved into the new cart safely.');
        }
      }

      const currentQuantities = quantitiesBySignature(cart.items);
      const originalDesired = parkedDesiredQuantities(parked);
      if (parked.stage === 'pending') {
        if (sameSignatureQuantities(currentQuantities, originalDesired)) {
          removeSessionValue(parkedStorageKey);
          return cart;
        }
        if (!sameSignatureQuantities(currentQuantities, parked.expectedSelected || {})) {
          throw new Error('Your cart changed while checkout was opening. Please review it before trying again.');
        }
      }

      if (parked.stage !== 'restoring') {
        const alreadyRestored = parked.excluded.every((line) => (
          Number(currentQuantities[line.signature] || 0) >= Number(originalDesired[line.signature] || 0)
        ));
        if (alreadyRestored) {
          removeSessionValue(parkedStorageKey);
          return cart;
        }

        const desired = { ...currentQuantities };
        [...new Set(parked.excluded.map((line) => line.signature))].forEach((signature) => {
          const currentQuantity = Number(currentQuantities[signature] || 0);
          const selectedBaseline = Number(parked.expectedSelected?.[signature] || 0);
          if (currentQuantity < selectedBaseline) {
            throw new Error('A selected quantity changed before excluded items could be restored. Please review your cart.');
          }
          // Restore only the missing portion. If another tab already restored
          // some or all of this signature, its current quantity is the floor.
          desired[signature] = Math.max(currentQuantity, Number(originalDesired[signature] || 0));
        });
        parked = { ...parked, stage: 'restoring', desired };
        if (!writeSessionJSON(parkedStorageKey, parked)) {
          throw new Error('Your excluded items could not be restored safely in this browser.');
        }
      }

      const latestQuantities = quantitiesBySignature(cart.items);
      const additions = [];
      const payloadBySignature = new Map();
      parked.excluded.forEach((line) => {
        if (!payloadBySignature.has(line.signature)) payloadBySignature.set(line.signature, line);
      });
      Object.entries(parked.desired || {}).forEach(([signature, desiredQuantity]) => {
        const missing = Number(desiredQuantity) - Number(latestQuantities[signature] || 0);
        if (missing <= 0) return;
        const stored = payloadBySignature.get(signature);
        if (!stored) throw new Error('A saved cart item could not be identified for restoration.');
        additions.push({ ...stored.payload, quantity: missing });
      });

      if (additions.length) {
        try {
          await fetchJSON('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: additions }),
          });
        } catch (_error) {
          // A disconnected response does not prove the add failed. The fresh
          // cart below decides whether another attempt would be safe.
        }
        cart = await getCart();
      }

      const restoredQuantities = quantitiesBySignature(cart.items);
      const restored = Object.entries(parked.desired || {}).every(
        ([signature, quantity]) => Number(restoredQuantities[signature] || 0) >= Number(quantity)
      );
      if (!restored) {
        throw new Error('One or more excluded items are currently unavailable. Their details are saved; close and reopen the cart to retry.');
      }

      removeSessionValue(parkedStorageKey);
      return cart;
    });

    try {
      return await restorePromise;
    } finally {
      restorePromise = null;
    }
  };

  const prepareSelectedCheckout = async (event) => {
    event.preventDefault();
    if (checkoutBusy) return;
    checkoutBusy = true;
    let navigating = false;
    if (el.checkout) el.checkout.disabled = true;
    el.lines.inert = true;

    try {
      if (readSessionJSON(parkedStorageKey)) {
        throw new Error('Saved items are still waiting to be restored. Restore them before starting another checkout.');
      }
      const cart = await getCart();
      const selectedItems = selectedItemsFor(cart);
      const excludedItems = cart.items.filter((item) => !lineIsSelected(item));
      if (!selectedItems.length) {
        render(cart);
        announce('Select at least one item before checkout.', true);
        el.lines.querySelector('[data-cart-select]')?.focus();
        return;
      }

      if (!excludedItems.length) {
        navigating = true;
        window.location.assign('/checkout');
        return;
      }

      const selectedKeys = selectedItems.map((item) => item.key);
      const excludedKeys = excludedItems.map((item) => item.key);
      const selectedKeySet = new Set(selectedKeys);
      const excludedKeySet = new Set(excludedKeys);
      const selectedSignatures = new Set(selectedItems.map(signatureForLine));
      const mixedSignature = excludedItems.some((item) => selectedSignatures.has(signatureForLine(item)));
      if (
        selectedKeySet.size !== selectedKeys.length
        || excludedKeySet.size !== excludedKeys.length
        || excludedKeys.some((key) => selectedKeySet.has(key))
        || mixedSignature
      ) {
        throw new Error('Matching split cart lines must use the same checkout selection. Please select them the same way and try again.');
      }
      if (!navigator.locks?.request) {
        throw new Error('Selected-item checkout needs a browser with secure cart locking. No cart items were changed.');
      }

      const parked = {
        version: 1,
        stage: 'pending',
        cartToken: cart.token,
        createdAt: Date.now(),
        expectedSelected: quantitiesBySignature(selectedItems),
        excluded: excludedItems.map((item) => ({
          key: item.key,
          signature: signatureForLine(item),
          quantity: Number(item.quantity),
          payload: payloadForLine(item),
        })),
      };
      if (!writeSessionJSON(parkedStorageKey, parked)) {
        throw new Error('Checkout selection could not be saved safely in this browser.');
      }

      const updates = {};
      excludedItems.forEach((item) => { updates[item.key] = 0; });
      await fetchJSON('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      const checkoutCart = await getCart();
      const checkoutQuantities = quantitiesBySignature(checkoutCart.items);
      if (
        checkoutCart.token !== cart.token
        || !sameSignatureQuantities(checkoutQuantities, parked.expectedSelected)
      ) {
        throw new Error('The selected quantities could not be verified for checkout.');
      }

      parked.stage = 'parked';
      if (!writeSessionJSON(parkedStorageKey, parked)) {
        throw new Error('Checkout selection could not be confirmed safely in this browser.');
      }
      navigating = true;
      window.location.assign('/checkout');
    } catch (error) {
      try {
        const restoredCart = await restoreParkedCart();
        if (restoredCart) render(restoredCart);
      } catch (restoreError) {
        try { render(await getCart()); } catch (_refreshError) { /* Keep the current drawer visible. */ }
        announce(restoreError.message || 'Your excluded items are saved and will be restored automatically.', true);
        return;
      }
      announce(error.message || 'Checkout could not be prepared. Your cart was restored.', true);
    } finally {
      if (!navigating) {
        checkoutBusy = false;
        el.lines.inert = false;
        if (currentCart) render(currentCart);
      }
    }
  };

  el.checkout?.form?.addEventListener('submit', prepareSelectedCheckout);

  const hideToast = () => {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.hidden = true;
  };

  const openDrawer = async (trigger) => {
    hideToast();
    lastCartTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    try {
      const restoredCart = await restoreParkedCart();
      const cart = restoredCart || await getCart();
      if (customerAcceptsMarketing && !cartHasFreeShipping(cart)) {
        await ensureFreeShippingDiscount();
      }
      render(cart);
      drawer.showModal();
      setPageScrollLock(true);
      q('[data-cart-close]').focus();
    } catch (error) {
      try {
        // Restoration can fail persistently (for example, a variant became
        // unavailable). Keep the cart usable and retain the parked payload;
        // closing and reopening the drawer is the explicit retry path.
        render(await getCart());
        drawer.showModal();
        setPageScrollLock(true);
        q('[data-cart-close]').focus();
        announce(error.message || 'Saved items could not be restored yet. Close and reopen the cart to retry.', true);
      } catch (_cartError) {
        setPageScrollLock(false);
        window.location.assign('/cart');
      }
    }
  };

  const showToast = (item) => {
    if (!toast) return;
    if (item) {
      if (item.image) {
        el.toastImage.src = item.image;
        el.toastImage.hidden = false;
      } else {
        el.toastImage.hidden = true;
      }
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? ` · ${item.variant_title}` : '';
      el.toastLine.textContent = item.product_title + variant;
    }
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  };

  /**
   * Product forms keep their native Shopify action as the no-JavaScript
   * fallback. With JavaScript available, add through the JSON endpoint so the
   * current product page stays in place while the cart drawer opens.
   */
  const addFromProductPage = async (event) => {
    const form = event.currentTarget;
    const submitter = event.submitter;
    if (submitter && !submitter.matches('[data-add-button]')) return;
    if (event.defaultPrevented) return;

    event.preventDefault();
    if (form.dataset.cartSubmitting === 'true') return;

    const addButton = submitter || form.querySelector('[data-add-button]');
    const addUrl = new URL(form.action, window.location.href);
    if (!addUrl.pathname.endsWith('.js')) {
      addUrl.pathname = `${addUrl.pathname.replace(/\/$/, '')}.js`;
    }

    form.dataset.cartSubmitting = 'true';
    addButton?.setAttribute('aria-busy', 'true');

    try {
      const item = await fetchJSON(addUrl.href, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item } }));
    } catch (error) {
      announce(error.message || 'This item could not be added to your cart.', true);
    } finally {
      delete form.dataset.cartSubmitting;
      addButton?.removeAttribute('aria-busy');
    }
  };

  document.querySelectorAll('form.product__form').forEach((form) => {
    form.addEventListener('submit', addFromProductPage);
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    openDrawer(trigger);
  });

  if (toast) toast.addEventListener('click', () => openDrawer(toast));
  el.shippingSignup?.addEventListener('click', () => {
    const signupOpen = document.querySelector('[data-free-shipping-open]');
    if (!(signupOpen instanceof HTMLElement)) {
      announce('Email signup is not available right now. Please try again later.', true);
      return;
    }
    drawer.close();
    requestAnimationFrame(() => signupOpen.click());
  });
  el.addItems?.addEventListener('click', openPicker);
  el.pickerBack?.addEventListener('click', closePicker);
  el.undoButton?.addEventListener('click', undoRemove);
  q('[data-cart-close]').addEventListener('click', () => drawer.close());
  drawer.addEventListener('click', (event) => {
    if (event.target === drawer) drawer.close();
  });
  drawer.addEventListener('cancel', (event) => {
    if (!el.picker.hidden) {
      event.preventDefault();
      closePicker();
    }
  });
  drawer.addEventListener('close', () => {
    hideUndo();
    setPageScrollLock(false);
    el.picker.hidden = true;
    el.head.inert = false;
    el.body.inert = false;
    el.addItems?.setAttribute('aria-expanded', 'false');
    if (lastCartTrigger?.isConnected) lastCartTrigger.focus();
  });

  document.addEventListener('cart:updated', async (event) => {
    const cart = await getCart();
    setBadge(cart.item_count);
    if (drawer.open) {
      render(cart);
      return;
    }

    /**
     * Open the drawer on an add rather than only confirming with a toast.
     *
     * The drawer is where basket management and Add Items live. A toast can
     * confirm the addition, while the drawer lets the shopper review and edit
     * the order before checkout.
     */
    if (drawer.dataset.openOnAdd === 'false') {
      showToast(event.detail && event.detail.item);
      return;
    }

    openDrawer(document.activeElement);
  });

  window.addEventListener('pageshow', async (event) => {
    const hasParkedItems = Boolean(readSessionJSON(parkedStorageKey));
    if (!event.persisted && !hasParkedItems) return;
    // A checkout navigation can leave this page in the back-forward cache
    // with its controls locked. Unlock before restoring the parked lines.
    checkoutBusy = false;
    el.lines.inert = false;
    try {
      const restoredCart = await restoreParkedCart();
      const visibleCart = restoredCart || (event.persisted ? await getCart() : null);
      if (visibleCart) {
        setBadge(visibleCart.item_count);
        if (drawer.open) render(visibleCart);
      }
    } catch (error) {
      announce(error.message || 'Saved cart items could not be restored yet.', true);
    }
  });

  // A visit to /cart redirects here with ?cart=open (see sections/cart.liquid):
  // show the drawer instead of a separate cart page, then strip the flag so a
  // refresh or shared link does not force it open again.
  (() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('cart')) return;
    params.delete('cart');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    );
    openDrawer(null);
  })();
})();
